import { type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Structural primitives for any `<table>` in CORTEX.
 *
 * The admin console (Audit Log, Sectors) and the pipeline (QuoteHistory)
 * previously each hand-rolled their own `<thead>` colour, column label
 * weight, and row hover. These sub-components are the single source of
 * truth for those styles so every table looks identical.
 *
 * Design rules baked in:
 *   - Header row: `bg-muted/40` + `border-b border-border` (follows theme,
 *     works in light and dark mode via semantic tokens).
 *   - Column labels: `text-micro font-bold text-muted-foreground uppercase
 *     tracking-wider` — small, all-caps, de-emphasised so data rows carry
 *     visual weight.
 *   - Body rows: `hover:bg-accent/50 transition-colors` for subtle
 *     interactivity without the branded indigo bleed of the pipeline.
 *   - Dividers: `divide-y divide-border` (semantic, not slate-100).
 *
 * Usage:
 * ```tsx
 * <DataTable>
 *   <DataTableHead>
 *     <DataTableHeader>Name</DataTableHeader>
 *     <DataTableHeader align="right">Amount</DataTableHeader>
 *   </DataTableHead>
 *   <DataTableBody>
 *     {rows.map(r => (
 *       <DataTableRow key={r.id}>
 *         <td className="px-4 py-3">{r.name}</td>
 *         <td className="px-4 py-3 text-right tabular-nums">{r.amount}</td>
 *       </DataTableRow>
 *     ))}
 *   </DataTableBody>
 * </DataTable>
 * ```
 *
 * If the table needs an `overflow-x-auto` scroll wrapper or a card border,
 * supply that at the call site — `DataTable` renders only the `<table>`
 * itself so container layout stays with the consumer.
 */

type DataTableProps = ComponentPropsWithoutRef<'table'>;

export function DataTable({ children, className, ...props }: DataTableProps) {
  return (
    <table className={cn('w-full text-left', className)} {...props}>
      {children}
    </table>
  );
}

interface DataTableHeadProps {
  readonly children: ReactNode;
}

export function DataTableHead({ children }: DataTableHeadProps) {
  return (
    <thead>
      <tr className="border-b border-border bg-muted/40">{children}</tr>
    </thead>
  );
}

interface DataTableHeaderProps {
  readonly children?: ReactNode;
  readonly align?: 'left' | 'right' | 'center';
  readonly className?: string;
}

export function DataTableHeader({ children, align = 'left', className }: DataTableHeaderProps) {
  return (
    <th
      className={cn(
        'px-4 py-3 text-micro font-bold text-muted-foreground uppercase tracking-wider',
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        className,
      )}
    >
      {children}
    </th>
  );
}

interface DataTableBodyProps {
  readonly children: ReactNode;
}

export function DataTableBody({ children }: DataTableBodyProps) {
  return <tbody className="divide-y divide-border text-xs">{children}</tbody>;
}

interface DataTableRowProps {
  readonly children: ReactNode;
  readonly className?: string;
}

export function DataTableRow({ children, className }: DataTableRowProps) {
  return <tr className={cn('hover:bg-accent/50 transition-colors', className)}>{children}</tr>;
}
